<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useMatterContextModalStore,
    isEventFieldModified,
    isEventModified,
    isEventQueuedForPublish,
    type EventRow,
    type EventTrackedField,
} from '../../stores/matterContextModalStore';
import { formatEventDate } from '../../utils/eventDate';
import MatterContextEditableField from './MatterContextEditableField.vue';

const { t } = useI18n();

// Date is required — it can't be cleared/saved empty.
const validateDate = (value: string): string | null =>
    value ? null : t('matterContext.eventDialog.dateRequired');

const props = defineProps<{
    visible: boolean;
    currentId: string | null;
    // When true the dialog edits a fresh, not-yet-persisted event held
    // locally (record nav hidden, footer shows "Add event"); the row is only
    // created in the store when the user confirms. Mirrors the entity dialog.
    createNew?: boolean;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:currentId', id: string): void;
}>();

const store = useMatterContextModalStore();

// Local draft backing create mode. Reset each time the dialog (re)opens in
// create mode so a prior cancelled add never leaks into the next one.
function blankEventDraft(): EventRow {
    const base = { date: '', context: '' };
    return { id: '__new-event__', slug: '', ...base, original: { ...base } };
}
const draftRow = ref<EventRow>(blankEventDraft());
watch(
    () => props.visible && !!props.createNew,
    (active) => {
        if (active) draftRow.value = blankEventDraft();
    },
);

const index = computed(() => store.events.findIndex((row) => row.id === props.currentId));
// Unified accessor: the local draft in create mode, the matched store row
// otherwise. Every field/handler below reads through this.
const event = computed(() =>
    props.createNew ? draftRow.value : index.value >= 0 ? store.events[index.value] : null,
);
const total = computed(() => store.events.length);

const displayName = computed(() => {
    const e = event.value;
    if (!e) return '';
    const d = formatEventDate(e.date);
    if (d) return d;
    const ctx = (e.context ?? '').trim();
    if (ctx) return ctx.length > 60 ? `${ctx.slice(0, 60)}…` : ctx;
    return props.createNew ? t('matterContext.eventDialog.newEvent') : t('matterContext.eventDialog.untitledEvent');
});

const atFirst = computed(() => index.value <= 0);
const atLast = computed(() => index.value < 0 || index.value >= total.value - 1);

// Edits write to the local draft in create mode, the store row otherwise.
const update = (patch: Parameters<typeof store.updateEvent>[1]) => {
    if (!event.value) return;
    if (props.createNew) Object.assign(draftRow.value, patch);
    else store.updateEvent(event.value.id, patch);
};

// No baseline-diff styling for a brand-new event.
const fieldModified = (field: EventTrackedField): boolean =>
    !props.createNew && event.value ? isEventFieldModified(event.value, field) : false;

const eventModified = computed(() =>
    !props.createNew && event.value ? isEventModified(event.value) : false,
);

const resetEvent = () => {
    if (event.value) store.resetEvent(event.value.id);
};

// Status switch (edit mode only): Active = included, Inactive = excluded
// (soft-deleted). Mirrors the row switch and reuses the same store actions.
// Blur after toggling so the styled tooltip doesn't stick open.
const toggleStatus = (e: Event) => {
    const row = event.value;
    if (!row) return;
    if (row.deleted) store.restoreEvent(row.id);
    else store.deleteEvent(row.id);
    (e.target as HTMLElement | null)?.blur();
};

// Field refs so we can detect / commit / discard in-progress edits before
// navigating or closing. The body is keyed by event id, so switching records
// remounts the fields in view mode.
type FieldApi = InstanceType<typeof MatterContextEditableField>;
const dateField = ref<FieldApi | null>(null);
const contextField = ref<FieldApi | null>(null);

const allFields = (): FieldApi[] =>
    [dateField, contextField].map((r) => r.value).filter((f): f is FieldApi => f !== null);

const anyDirty = () => allFields().some((f) => f.isDirty());
const saveAll = (): boolean => {
    let ok = true;
    for (const f of allFields()) if (!f.commit()) ok = false;
    return ok;
};

// There is no per-event Save: committed edits (and added rows) are QUEUED in
// the store and staged + published together when the user publishes the
// matter context. The footer surfaces that queued state for the current row.
const eventQueued = computed(() =>
    !props.createNew && event.value ? isEventQueuedForPublish(event.value) : false,
);

// Create mode: the Add button enables once a (committed) date is present —
// date is required and commits on ✓/Enter/blur. Commits all in-progress
// edits (running validators) then adds the row to the store, where it sits
// queued until publish. Mirrors the entity dialog.
const canCreate = computed(() => props.createNew && draftRow.value.date.trim().length > 0);
const createEvent = () => {
    if (!saveAll()) return; // a field failed validation (e.g. date)
    const d = draftRow.value;
    if (!d.date.trim()) return;
    store.addEvent(d.date, d.context);
    store.showToast(t('matterContext.eventDialog.addedToast'));
    emit('close');
};

type PendingAction = { type: 'navigate'; targetId: string } | { type: 'close' };
const pendingAction = ref<PendingAction | null>(null);

const goTo = (target: number) => {
    if (target < 0 || target >= total.value) return;
    const targetId = store.events[target].id;
    // Only an in-progress field edit blocks leaving — committed edits are
    // queued in the store and survive navigation.
    if (anyDirty()) {
        pendingAction.value = { type: 'navigate', targetId };
    } else {
        emit('update:currentId', targetId);
    }
};

const requestClose = () => {
    // Create mode: closing cancels the add outright — the draft is local and
    // unsaved, so there's nothing to warn about.
    if (props.createNew) {
        emit('close');
        return;
    }
    if (anyDirty()) {
        pendingAction.value = { type: 'close' };
    } else {
        emit('close');
    }
};

const performPending = () => {
    const action = pendingAction.value;
    pendingAction.value = null;
    if (!action) return;
    if (action.type === 'navigate') emit('update:currentId', action.targetId);
    else emit('close');
};

const onWarnCancel = () => {
    pendingAction.value = null;
};
// Abandon the in-progress field edit; committed (queued) changes are kept.
const onWarnDiscard = () => {
    performPending();
};
// Commit the in-progress field edits to the store (queueing them). If a
// field fails validation, stay put with the error surfaced.
const onWarnSave = () => {
    if (saveAll()) performPending();
    else pendingAction.value = null;
};
</script>

<template>
    <div v-if="visible && event" class="mc-profile-backdrop" @click.self="requestClose">
        <aside class="mc-profile" role="dialog" :aria-label="t('matterContext.eventDialog.ariaLabel')">
            <header class="mc-profile-header">
                <span class="mc-profile-eyebrow">{{
                    createNew ? t('matterContext.eventDialog.newEvent') : t('matterContext.eventDialog.eyebrow')
                }}</span>
                <button type="button" class="mc-profile-close" :title="t('common.close')" @click="requestClose">
                    <span class="material-icons" style="font-size: 18px">close</span>
                </button>
            </header>

            <div class="mc-profile-subheader">
                <h2 class="mc-profile-title">{{ displayName }}</h2>
                <div v-if="!createNew" class="mc-profile-nav">
                    <span class="mc-profile-record">
                        {{ t('matterContext.common.recordLabel') }} <strong>{{ index + 1 }}</strong> /{{ total }}
                    </span>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.first')"
                        :disabled="atFirst"
                        @click="goTo(0)"
                    >
                        <span class="material-icons" style="font-size: 18px">first_page</span>
                    </button>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.previous')"
                        :disabled="atFirst"
                        @click="goTo(index - 1)"
                    >
                        <span class="material-icons" style="font-size: 18px">chevron_left</span>
                    </button>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.next')"
                        :disabled="atLast"
                        @click="goTo(index + 1)"
                    >
                        <span class="material-icons" style="font-size: 18px">chevron_right</span>
                    </button>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.last')"
                        :disabled="atLast"
                        @click="goTo(total - 1)"
                    >
                        <span class="material-icons" style="font-size: 18px">last_page</span>
                    </button>
                </div>
            </div>

            <div :key="event.id" class="mc-profile-body">
                <section class="mc-profile-card">
                    <h3 class="mc-profile-card-title">{{ t('matterContext.eventDialog.aboutCard') }}</h3>
                    <div v-if="!createNew" class="mc-field mc-field-status">
                        <div class="mc-field-label">{{ t('matterContext.common.status') }}</div>
                        <div class="mc-field-value">
                            <label class="mc-switch-wrap mc-status-switch">
                                <span class="mc-switch">
                                    <input
                                        type="checkbox"
                                        class="mc-switch-input"
                                        :checked="!event.deleted"
                                        @change="toggleStatus($event)"
                                    />
                                    <span class="mc-switch-track">
                                        <span class="mc-switch-thumb"></span>
                                    </span>
                                </span>
                                <span
                                    class="mc-status-label"
                                    :class="{ 'mc-status-label-active': !event.deleted }"
                                    >{{ event.deleted ? t('matterContext.common.inactive') : t('matterContext.common.active') }}</span
                                >
                                <span
                                    class="mc-switch-tooltip mc-switch-tooltip-below"
                                    role="tooltip"
                                >
                                    <!-- eslint-disable-next-line vue/no-v-html -- static i18n string with a bolded action word, no user data -->
                                    <span v-html="t(event.deleted ? 'matterContext.events.tooltipExclude' : 'matterContext.events.tooltipInclude')"></span>
                                </span>
                            </label>
                        </div>
                    </div>
                    <MatterContextEditableField
                        ref="dateField"
                        :label="t('matterContext.events.colDate')"
                        :value="event.date"
                        :modified="fieldModified('date')"
                        edit-type="date"
                        :add-label="t('matterContext.eventDialog.addDate')"
                        :validate="validateDate"
                        @save="(v) => update({ date: v })"
                    />
                    <MatterContextEditableField
                        ref="contextField"
                        :label="t('matterContext.events.colContext')"
                        :value="event.context"
                        :modified="fieldModified('context')"
                        edit-type="textarea"
                        :placeholder="t('matterContext.common.addContext')"
                        @save="(v) => update({ context: v })"
                    />
                </section>
            </div>

            <footer v-if="createNew" class="mc-profile-footer">
                <button
                    type="button"
                    class="mc-profile-save"
                    :disabled="!canCreate"
                    @click="createEvent"
                >
                    {{ t('matterContext.eventDialog.addEventButton') }}
                </button>
            </footer>
            <footer v-else-if="eventQueued" class="mc-profile-footer">
                <button
                    v-if="eventModified"
                    type="button"
                    class="mc-profile-reset"
                    @click="resetEvent"
                >
                    <span class="material-icons" style="font-size: 16px">restart_alt</span>
                    {{ t('matterContext.common.resetChanges') }}
                </button>
                <span class="mc-profile-queued-note">
                    <span class="mc-row-mod-dot" aria-hidden="true"></span>
                    {{ t('matterContext.common.changesInDraft') }}
                    <span class="mc-draft-tooltip" role="tooltip">
                        {{ t('matterContext.common.draftTooltip') }}
                    </span>
                </span>
            </footer>

            <div v-if="pendingAction" class="mc-confirm-backdrop" @click.self="onWarnCancel">
                <div class="mc-confirm" role="alertdialog" :aria-label="t('matterContext.common.unsavedChangesTitle')">
                    <h3 class="mc-confirm-title">{{ t('matterContext.common.unsavedChangesTitle') }}</h3>
                    <p class="mc-confirm-body">
                        {{ t('matterContext.common.unsavedChangesBody') }}
                    </p>
                    <div class="mc-confirm-actions">
                        <button type="button" class="mc-confirm-btn" @click="onWarnCancel">
                            {{ t('common.cancel') }}
                        </button>
                        <button
                            type="button"
                            class="mc-confirm-btn mc-confirm-btn-danger"
                            @click="onWarnDiscard"
                        >
                            {{ t('matterContext.common.discardChanges') }}
                        </button>
                        <button
                            type="button"
                            class="mc-confirm-btn mc-confirm-btn-primary"
                            @click="onWarnSave"
                        >
                            {{ t('matterContext.common.saveChanges') }}
                        </button>
                    </div>
                </div>
            </div>
        </aside>
    </div>
</template>
